import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

import useV3OfferAutosave from '../hooks/useV3OfferAutosave';
import { useV3OfferContext, V3_ACTIONS } from '../contexts/V3OfferContext';
import V3OfferDealTermsHeader from './V3OfferDealTermsHeader';
import V3OfferDealTermsForm from './V3OfferDealTermsForm';
import V3DealTermsCard from './V3DealTermsCard';
import V3DealTermsGuaranteeNotesEditor from './V3DealTermsGuaranteeNotesEditor';
import V3OfferAdditionalTermsSection from './V3OfferAdditionalTermsSection';
import V3BudgetBonusManager from './V3BudgetBonusManager';
import V3DealBracketsEditor from './V3DealBracketsEditor';
import V3OfferContactsCard from './V3OfferContactsCard';
import V3PoolDealSection from './V3PoolDealSection';
import { isPoolPreviewActive } from '../utils/buildPoolEngineInput';
import V3PoolMemberShare from './V3PoolMemberShare';
import V3PoolSplitPreview from './V3PoolSplitPreview';
import useV3Budgets from '../hooks/useV3Budgets';
import { poolShareOfGross } from '../../../shared/poolShareOfGross';

const V3OfferDealTermsPaneBody = ({
  team,
  offer,
  budget,
  confirm,
  csrfToken,
  actType,
  showToolbar,
  onPreview,
  onCopy,
  onCopyHolds,
  canCopyHolds,
  onDownload,
  onCreateHold,
  onCreateConfirm,
  canConfirm,
  onDelete,
  onSend,
  onAddContact,
  onChangeActType,
  bonusManagerProps,
  walkoutPotential,
  onEditPerformer,
  currentUser,
  offerContacts,
  offerContactsChanged,
  contactsDefaultOpen,
  calculated,
}) => {
  const ctx = useV3OfferContext();

  // O-2783 "pool over offers". The pool overlay + lineup live on the canonical
  // context offer (the toggle/pool hook mutate it there). Pool-mode controls
  // render only when entitled, in Pool mode, and the group has 2+ members.
  const contextOffer = ctx?.offer;
  const pool = contextOffer?.v3_offer_pool || null;
  const lineupCount = (contextOffer?.lineup || []).length;
  const poolEntitled = !!team?.package?.multi_artist_v3_offers;
  // Pool controls show with even a single artist so the booker can configure the
  // pool deal + this artist's share while building the lineup one at a time.
  const showPoolControls = poolEntitled && pool?.mode === 'pool' && lineupCount >= 1;

  const {
    localOffer,
    setLocalOffer,
    localBudget,
    setLocalBudget,
    updateLocalOffer,
    updatePoolShareField,
    updateLocalDeposit,
    isSaving,
    cancelPendingSave,
  } = useV3OfferAutosave({ team, csrfToken });

  const { deleteDealBracket } = useV3Budgets();

  useEffect(() => {
    cancelPendingSave();
    setLocalOffer(offer || null);
    setLocalBudget(budget || null);
    // Re-sync when offer/budget identity changes (swapping siblings) OR
    // when an external SET_OFFER dispatch lands new server-derived values:
    //  - artistable_id flips on a Change-artist swap (the Performer record
    //    is PATCHed in place, so performer.id stays the same — only the
    //    artistable_id rotates)
    //  - act_type flips on a role change
    //  - template_id flips on a template apply / unlink
    //  - state flips on send/accept/decline
    //  - v3_offer_pool_id flips when entering/leaving Pool mode IN-SESSION
    //    (O-2783): the toggle's applyPool dispatches it onto the context offer,
    //    so the shadow MUST reseed or the next per-member share PATCH would be
    //    built from a stale shadow whose v3_offer_pool_id is still null —
    //    buildV3OfferPayload then drops pool_percentage/pool_payout_strategy and
    //    the edit silently fails to persist. No in-flight deal edit exists at
    //    toggle time, so reseeding here is safe.
    // User keystrokes hit updateLocalOffer and only touch the shadow, so
    // they never trigger a resync here.
  }, [
    offer?.id,
    budget?.id,
    offer?.primary_performer?.artistable_id,
    offer?.primary_performer?.act_type,
    offer?.template_id,
    offer?.state,
    offer?.v3_offer_pool_id,
  ]);

  // Two ways the shadow reconciles, deliberately: the effect above REPLACES it
  // when the offer's identity changes, this one PATCHES a single field when its
  // value changes underneath us. A field belongs here rather than in the list
  // above when a reseed would be destructive — the reseed cancels the pending
  // autosave, so it would discard an in-flight deal-terms edit.
  //
  // apply_tax_after_facility_fee is the only such field today: marking a budget
  // row writes it server-side (O-4778), so it arrives as an external SET_OFFER
  // while the user may still be typing in this pane. Adding a second one means
  // extending this effect, not writing a third mechanism.
  useEffect(() => {
    const next = offer?.apply_tax_after_facility_fee;
    setLocalOffer((prev) => (
      prev && !!prev.apply_tax_after_facility_fee !== !!next
        ? { ...prev, apply_tax_after_facility_fee: next }
        : prev
    ));
  }, [offer?.apply_tax_after_facility_fee]);

  const displayOffer = localOffer || offer;
  const displayBudget = localBudget || budget;
  const deposits = displayBudget?.v3_budget_deposits || [];

  const latestDisplayOfferRef = useRef(displayOffer);
  latestDisplayOfferRef.current = displayOffer;

  const guaranteeNotesEditor = useMemo(() => () => (
    <V3DealTermsGuaranteeNotesEditor
      offer={latestDisplayOfferRef.current}
      onChangeField={updateLocalOffer}
    />
  ), [displayOffer?.id]);

  const dispatchRef = useRef(ctx?.dispatch);
  dispatchRef.current = ctx?.dispatch;

  const additionalTermsEditor = useMemo(() => () => (
    <V3OfferAdditionalTermsSection
      team={team}
      csrfToken={csrfToken}
      offer={latestDisplayOfferRef.current}
      embedded
      onSaved={(html) => {
        setLocalOffer((prev) => (prev ? { ...prev, additional_terms: html } : prev));
        dispatchRef.current?.({ type: V3_ACTIONS.UPDATE_OFFER, payload: { additional_terms: html } });
      }}
    />
  ), [displayOffer?.id, team?.id, csrfToken]);

  const BonusManager = V3BudgetBonusManager;

  // Deal brackets live on the context budget (V3DealBracketsEditor reads/writes
  // it through useV3Budgets); the config item reads `dealBrackets` for its
  // configured/summary state and clears via the persisting handler.
  const dealBrackets = ctx?.budget?.v3_deal_brackets || [];

  const dealTermsCtx = useMemo(() => ({
    offer: displayOffer || {},
    deposits,
    walkoutPotential: walkoutPotential || 0,
    bonusManagerProps,
    dealBrackets,
    DealBracketsEditor: V3DealBracketsEditor,
    poolMode: isPoolPreviewActive(contextOffer, contextOffer?.lineup),
    clearDealBrackets: () => {
      dealBrackets.forEach((bracket) => { if (bracket.id) deleteDealBracket(bracket.id); });
    },
    onChangeField: updateLocalOffer,
    onChangeDeposit: updateLocalDeposit,
    team,
    csrfToken,
    BonusManager,
    GuaranteeNotesEditor: guaranteeNotesEditor,
    AdditionalTermsEditor: additionalTermsEditor,
  }), [
    displayOffer,
    deposits,
    walkoutPotential,
    bonusManagerProps,
    dealBrackets,
    contextOffer,
    deleteDealBracket,
    updateLocalOffer,
    updateLocalDeposit,
    team,
    csrfToken,
    guaranteeNotesEditor,
    additionalTermsEditor,
  ]);

  if (!displayOffer) return null;

  return (
    <>
      <V3OfferDealTermsHeader
        offer={displayOffer}
        confirm={confirm}
        showActions={showToolbar}
        onPreview={onPreview}
        onCopy={onCopy}
        onCopyHolds={onCopyHolds}
        canCopyHolds={canCopyHolds}
        onDownload={onDownload}
        onCreateHold={onCreateHold}
        onCreateConfirm={onCreateConfirm}
        canConfirm={canConfirm}
        onDelete={onDelete}
        onSend={onSend}
        onAddContact={onAddContact}
        onEditPerformer={onEditPerformer}
        switchEditorUrl={team && displayOffer ? `/teams/${team.id}/offers/v3_simple/${displayOffer.id}/edit` : null}
      />

      {isSaving && (
        <div className="deal-terms-pane__saving-row">
          <span className="deal-terms-pane__saving">Saving…</span>
        </div>
      )}

      <V3OfferDealTermsForm
        offer={displayOffer}
        actType={actType}
        onChangeField={updateLocalOffer}
        onChangeActType={onChangeActType}
        poolMode={showPoolControls}
        calculated={calculated}
      />

      {showPoolControls && (
        <>
          <V3PoolMemberShare
            offer={displayOffer}
            earnings={calculated?.artistEarnings || null}
            onChangeField={updatePoolShareField}
          />
          <V3PoolDealSection
            pool={pool}
            team={team}
            poolSharePercent={poolShareOfGross(calculated?.artistEarnings)}
            dealBracketReached={calculated?.artistEarnings?.dealBracketReached || null}
          />
          <V3PoolSplitPreview
            poolMembers={calculated?.poolMembers || []}
            poolAmount={calculated?.artistEarnings?.poolAmount || 0}
            grossPotential={calculated?.artistEarnings?.grossPotential || 0}
            dealBracketReached={calculated?.artistEarnings?.dealBracketReached || null}
            oversubscribed={calculated?.artistEarnings?.oversubscribed || false}
            unallocated={calculated?.artistEarnings?.unallocated || 0}
            lineup={contextOffer?.lineup || []}
            team={team}
          />
        </>
      )}

      <V3DealTermsCard ctx={dealTermsCtx} />

      {/* Card is hidden when the offer has no contacts (persisted or pending);
        * the deal-terms-header's "+ Add contacts" link is the entry point for
        * the first contact, which adds an unsaved placeholder that brings the
        * card into the DOM. */}
      {currentUser && offerContactsChanged && (offerContacts || []).length > 0 && (
        <V3OfferContactsCard
          csrfToken={csrfToken}
          team={team}
          offer={displayOffer}
          currentUser={currentUser}
          offerContacts={offerContacts}
          offerContactsChanged={offerContactsChanged}
          defaultOpen={contactsDefaultOpen}
          onRequestAddContact={onAddContact}
        />
      )}
    </>
  );
};

V3OfferDealTermsPaneBody.propTypes = {
  team: PropTypes.object.isRequired,
  offer: PropTypes.object,
  budget: PropTypes.object,
  confirm: PropTypes.object,
  csrfToken: PropTypes.string.isRequired,
  actType: PropTypes.string,
  showToolbar: PropTypes.bool,
  onPreview: PropTypes.func,
  onCopy: PropTypes.func,
  onCopyHolds: PropTypes.func,
  canCopyHolds: PropTypes.bool,
  onDownload: PropTypes.func,
  onCreateHold: PropTypes.func,
  onCreateConfirm: PropTypes.func,
  canConfirm: PropTypes.bool,
  onDelete: PropTypes.func,
  onSend: PropTypes.func,
  onAddContact: PropTypes.func,
  onChangeActType: PropTypes.func.isRequired,
  bonusManagerProps: PropTypes.object,
  walkoutPotential: PropTypes.number,
  onEditPerformer: PropTypes.func,
  currentUser: PropTypes.object,
  offerContacts: PropTypes.array,
  offerContactsChanged: PropTypes.func,
  contactsDefaultOpen: PropTypes.bool,
  calculated: PropTypes.object,
};

V3OfferDealTermsPaneBody.defaultProps = {
  offer: null,
  budget: null,
  confirm: null,
  actType: null,
  showToolbar: true,
  onPreview: undefined,
  onCopy: undefined,
  onCopyHolds: undefined,
  canCopyHolds: false,
  onDownload: undefined,
  onCreateHold: undefined,
  onCreateConfirm: null,
  canConfirm: false,
  onDelete: undefined,
  onSend: undefined,
  onAddContact: null,
  bonusManagerProps: null,
  walkoutPotential: 0,
  onEditPerformer: null,
  currentUser: null,
  offerContacts: [],
  offerContactsChanged: null,
  contactsDefaultOpen: false,
  calculated: null,
};

export default V3OfferDealTermsPaneBody;
